// Crystal Rock Accessories — theme interactions
// Ported from the approved prototype's main.js. GSAP loaded via CDN
// (load-triggered tweens: hero stagger, gem draw-in, small UI feedback).
// Scroll-linked behavior (reveals, parallax) is native IntersectionObserver
// + rAF — no ScrollTrigger dependency. Everything degrades gracefully: if
// GSAP isn't present, base CSS states still render correctly.
//
// Real-store change from the prototype: "Add To Cart" / "Quick Add" now
// call Shopify's real cart AJAX API (POST /cart/add.js) instead of a fake
// setTimeout that only changed the button label — see initQuickAdd() below.

document.documentElement.classList.remove('no-js');

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const hasGsap = typeof window.gsap !== 'undefined';

/**
 * Safety net for entrance animations. GSAP drives everything via
 * requestAnimationFrame — on a throttled tab (background tab, low-power
 * mode, a stalled/blocked CDN mid-timeline) a rAF-driven tween can simply
 * never progress, which would otherwise leave content permanently hidden
 * at its "from" state. This clears the inline styles GSAP set, after a
 * timeout longer than the animation should ever take, so content is
 * guaranteed visible either way. Idempotent: a no-op if the animation
 * already finished normally.
 */
function revealFallback(elements, timeoutMs = 2200) {
  const list = elements instanceof NodeList || Array.isArray(elements) ? elements : [elements];
  setTimeout(() => {
    list.forEach((el) => {
      if (!el) return;
      el.style.opacity = '';
      el.style.transform = '';
      el.style.filter = '';
      if (el.style.strokeDashoffset !== undefined) el.style.strokeDashoffset = '0';
    });
  }, timeoutMs);
}

/* ---------------- Money formatting ----------------
   Mirrors Shopify's standard formatMoney helper against the store's own
   `shop.money_format` (injected by the layout), so every price shown by
   JS matches the store's real currency settings instead of a hardcoded
   "R" prefix. Amount is expected in the shop's base currency units (not
   cents) — matches what the theme's own Liquid-built product feed and the
   product page's `product | json` both give us. */
function formatMoney(amount, format) {
  format = format || window.__shopMoneyFormat || '${{amount}}';
  const cents = Math.round(Number(amount) * 100) || 0;
  function withDelimiters(numberStr, thousands, decimal) {
    const parts = numberStr.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts.join(decimal);
  }
  const value = withDelimiters((cents / 100).toFixed(2), ',', '.');
  const valueNoDecimals = withDelimiters(String(Math.round(cents / 100)), ',', '.');
  return format
    .replace(/\{\{\s*amount\s*\}\}/, value)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, valueNoDecimals)
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withDelimiters((cents / 100).toFixed(2), '.', ','));
}
window.formatMoney = formatMoney;

/* ---------------- Nav: solid on scroll ---------------- */
(function initNav() {
  const nav = document.querySelector('[data-nav]');
  if (!nav) return;
  const toggleState = () => {
    if (window.scrollY > 24) nav.classList.add('is-scrolled');
    else nav.classList.remove('is-scrolled');
  };
  toggleState();
  window.addEventListener('scroll', toggleState, { passive: true });
})();

/* ---------------- Mobile menu ---------------- */
(function initMobileMenu() {
  const openBtn = document.querySelector('[data-menu-open]');
  const closeBtn = document.querySelector('[data-menu-close]');
  const menu = document.querySelector('[data-mobile-menu]');
  const scrim = document.querySelector('[data-menu-scrim]');
  if (!openBtn || !menu) return;
  const open = () => { menu.classList.add('is-open'); scrim?.classList.remove('hidden'); document.body.style.overflow = 'hidden'; };
  const close = () => { menu.classList.remove('is-open'); scrim?.classList.add('hidden'); document.body.style.overflow = ''; };
  openBtn.addEventListener('click', open);
  closeBtn?.addEventListener('click', close);
  scrim?.addEventListener('click', close);
})();

/* ---------------- Hero: blur-in text reveal ---------------- */
(function initHeroReveal() {
  const headline = document.querySelector('[data-hero-headline]');
  if (!headline) return;

  const words = headline.textContent.trim().split(/\s+/);
  headline.innerHTML = words
    .map((w) => `<span class="hero-word inline-block">${w}</span>`)
    .join(' ');
  const spans = headline.querySelectorAll('.hero-word');

  const rest = document.querySelectorAll('[data-hero-fade]');

  if (!hasGsap || prefersReducedMotion) {
    spans.forEach((s) => { s.style.filter = 'none'; s.style.opacity = '1'; });
    rest.forEach((el) => { el.style.filter = 'none'; el.style.opacity = '1'; });
    return;
  }

  gsap.set(spans, { opacity: 0, y: 18, filter: 'blur(18px)' });
  gsap.set(rest, { opacity: 0, y: 14, filter: 'blur(10px)' });

  const tl = gsap.timeline({ delay: 0.3 });
  tl.to(spans, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 1.1, ease: 'power2.out', stagger: 0.08 })
    .to(rest, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.9, ease: 'power2.out', stagger: 0.12 }, '-=0.75');

  revealFallback(spans, 2400);
  revealFallback(rest, 2400);
})();

/* ---------------- Scroll reveals ---------------- */
(function initScrollReveals() {
  const singles = document.querySelectorAll('[data-reveal]');
  const groups = document.querySelectorAll('[data-reveal-group]');
  if (!singles.length && !groups.length) return;

  const revealNow = () => {
    singles.forEach((el) => el.classList.add('is-visible'));
    document.querySelectorAll('[data-reveal-item]').forEach((el) => el.classList.add('is-visible'));
  };

  if (prefersReducedMotion || !('IntersectionObserver' in window)) {
    revealNow();
    return;
  }

  groups.forEach((group) => {
    group.querySelectorAll('[data-reveal-item]').forEach((item, i) => {
      item.style.setProperty('--reveal-delay', `${Math.min(i, 8) * 70}ms`);
    });
  });

  requestAnimationFrame(() => {
    const io = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    singles.forEach((el) => io.observe(el));
    document.querySelectorAll('[data-reveal-item]').forEach((item) => io.observe(item));
  });

  setTimeout(() => {
    document.querySelectorAll('[data-reveal]:not(.is-visible), [data-reveal-item]:not(.is-visible)')
      .forEach((el) => el.classList.add('is-visible'));
  }, 4000);
})();

/* ---------------- SVG line-art draw-in ---------------- */
(function initLineDraw() {
  const paths = document.querySelectorAll('[data-draw]');
  if (!paths.length) return;

  if (!hasGsap || prefersReducedMotion || !('IntersectionObserver' in window)) {
    paths.forEach((p) => { p.style.strokeDashoffset = 0; });
    return;
  }

  const draw = (path) => {
    const length = path.getTotalLength ? path.getTotalLength() : 300;
    path.style.strokeDasharray = length;
    path.style.strokeDashoffset = length;
    gsap.to(path, { strokeDashoffset: 0, duration: 1.6, ease: 'power2.inOut' });
    revealFallback(path, 2600);
  };

  const io = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.querySelectorAll('[data-draw]').forEach(draw);
      observer.unobserve(entry.target);
    });
  }, { threshold: 0.2 });

  const seen = new Set();
  paths.forEach((path) => {
    const target = path.closest('[data-draw-trigger]') || path;
    if (seen.has(target)) return;
    seen.add(target);
    io.observe(target);
  });
})();

/* ---------------- Subtle hero parallax ---------------- */
(function initParallax() {
  const wraps = document.querySelectorAll('[data-parallax-wrap]');
  if (!wraps.length || prefersReducedMotion) return;

  let ticking = false;
  const active = new Set();

  function update() {
    ticking = false;
    active.forEach((wrap) => {
      const layer = wrap.querySelector('[data-parallax]');
      if (!layer) return;
      const rect = wrap.getBoundingClientRect();
      const mid = rect.top + rect.height / 2;
      const progress = (window.innerHeight / 2 - mid) / window.innerHeight;
      const speed = parseFloat(layer.dataset.parallax) || 0.15;
      const offset = progress * speed * 200;
      layer.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`;
    });
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  }

  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) active.add(entry.target);
      else active.delete(entry.target);
    });
    if (active.size) {
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    } else {
      window.removeEventListener('scroll', onScroll);
    }
  }, { threshold: 0 });

  wraps.forEach((wrap) => io.observe(wrap));
})();

/* ---------------- Hero video ---------------- */
(function initHeroVideo() {
  const video = document.querySelector('[data-hero-video]');
  const wrap = document.querySelector('[data-hero-video-wrap]');
  const scrim = document.querySelector('[data-hero-video-scrim]');
  if (!video || !wrap) return;

  if (prefersReducedMotion) {
    video.removeAttribute('autoplay');
    video.pause();
    return;
  }

  const tryPlay = () => { video.play().catch(() => {}); };

  function resume() {
    const atEnd = video.ended || (video.duration && video.currentTime >= video.duration - 0.05);
    if (atEnd) video.currentTime = 0;
    tryPlay();
  }
  function pause() {
    video.pause();
  }

  let ticking = false;
  function updateScrim() {
    ticking = false;
    const rect = wrap.getBoundingClientRect();
    const progress = Math.min(Math.max(-rect.top / rect.height, 0), 1);
    scrim.style.opacity = progress.toFixed(3);
  }
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(updateScrim);
  }

  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        resume();
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
      } else {
        pause();
        window.removeEventListener('scroll', onScroll);
      }
    });
  }, { threshold: 0 });

  io.observe(wrap);
  tryPlay();
})();

/* ---------------- Wishlist heart toggle ----------------
   Prototype-only affordance — no real wishlist backend exists on Shopify
   without a dedicated app, so this stays a local, cosmetic toggle exactly
   like the approved design (nothing here claims to persist anything). */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-wishlist]');
  if (!btn) return;
  e.preventDefault();
  btn.classList.toggle('is-active');
  if (hasGsap && !prefersReducedMotion) {
    gsap.fromTo(btn, { scale: 0.7 }, { scale: 1, duration: 0.4, ease: 'back.out(3)' });
  }
});

/* ---------------- Quick Add (collection grid / related / carousel) ----------------
   Real Shopify cart write: POST /cart/add.js with the product's first
   available variant, then re-sync the header cart count from the real
   cart (GET /cart.js) rather than just incrementing a local counter — so
   the number shown always matches what's actually in the customer's cart.
   The dedicated Add To Cart button on the product page (product.js) has
   its own handler instead of this one, because it must add whichever
   variant the shopper actually selected, not a fixed default. */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-add-cart]');
  if (!btn || btn.closest('[data-pd-root]')) return; // product page owns its own button
  e.preventDefault();
  if (btn.dataset.state === 'loading' || btn.dataset.state === 'done') return;

  const variantId = btn.dataset.variantId;
  const originalLabel = btn.querySelector('[data-label]');
  const label = originalLabel ? originalLabel.textContent : btn.textContent;

  if (!variantId) {
    // No purchasable variant on this card (e.g. sold out) — send the
    // shopper to the product page to choose options instead of silently
    // failing or lying about success.
    const link = btn.closest('a[href]');
    if (link) window.location.href = link.href;
    return;
  }

  btn.dataset.state = 'loading';
  btn.style.pointerEvents = 'none';
  if (originalLabel) originalLabel.textContent = 'Adding…';

  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
  })
    .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
    .then(({ ok, data }) => {
      if (!ok) throw new Error((data && data.description) || 'Could not add to cart');
      if (originalLabel) originalLabel.textContent = 'Added ✓';
      btn.dataset.state = 'done';
      return refreshCartCount();
    })
    .catch(() => {
      if (originalLabel) originalLabel.textContent = 'Sold Out';
      btn.dataset.state = 'done';
    })
    .finally(() => {
      setTimeout(() => {
        btn.dataset.state = '';
        btn.style.pointerEvents = '';
        if (originalLabel) originalLabel.textContent = label;
      }, 1600);
    });
});

function refreshCartCount() {
  return fetch('/cart.js', { headers: { Accept: 'application/json' } })
    .then((res) => res.json())
    .then((cart) => {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.classList.toggle('hidden', cart.item_count === 0);
        if (hasGsap && !prefersReducedMotion) {
          gsap.fromTo(el, { scale: 1.5 }, { scale: 1, duration: 0.35, ease: 'back.out(3)' });
        }
      });
      return cart;
    })
    .catch(() => {});
}
window.refreshCartCount = refreshCartCount;

/* ---------------- Quantity stepper ---------------- */
document.querySelectorAll('[data-qty]').forEach((wrap) => {
  const input = wrap.querySelector('[data-qty-value]');
  wrap.querySelectorAll('[data-qty-step]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const step = parseInt(btn.dataset.qtyStep, 10);
      const current = parseInt(input.value, 10) || 1;
      input.value = Math.max(1, current + step);
    });
  });
});

/* ---------------- Accordion (product details) ---------------- */
document.querySelectorAll('[data-accordion-trigger]').forEach((trigger) => {
  trigger.addEventListener('click', () => {
    const item = trigger.closest('.accordion-item');
    item.classList.toggle('is-open');
  });
});

/* ---------------- Best Sellers: 3D rotating carousel ---------------- */
(function initBestSellersCarousel() {
  const wrap = document.querySelector('[data-carousel]');
  const track = document.querySelector('[data-carousel-track]');
  if (!wrap || !track) return;

  const cards = Array.from(track.querySelectorAll('[data-carousel-card]'));
  if (!cards.length) return;

  const count = cards.length;
  track.style.setProperty('--count', count);
  cards.forEach((card, i) => card.style.setProperty('--i', i));

  function computeRadius() {
    const cardWidth = cards[0].offsetWidth || 240;
    const angle = Math.PI / count;
    const radius = Math.round((cardWidth / 2) / Math.tan(angle) * 1.4);
    track.style.setProperty('--radius', `${radius}px`);
  }
  computeRadius();
  window.addEventListener('resize', computeRadius);

  if (prefersReducedMotion) {
    wrap.classList.add('is-static');
  }

  const supportsHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  let activeCard = null;
  function setActive(card) {
    if (card === activeCard) card = null;
    cards.forEach((c) => c.classList.toggle('is-active', c === card));
    wrap.classList.toggle('is-paused', !!card);
    activeCard = card;
  }
  if (!supportsHover) {
    cards.forEach((card) => {
      card.addEventListener('click', (e) => {
        if (activeCard !== card) {
          e.preventDefault();
          setActive(card);
        }
      });
    });
    document.addEventListener('click', (e) => {
      if (activeCard && !e.target.closest('[data-carousel-card]')) setActive(null);
    });
  }
})();

/* ---------------- Testimonial crossfade ---------------- */
(function initTestimonials() {
  const slides = document.querySelectorAll('[data-testimonial]');
  if (slides.length < 2) return;
  let index = 0;
  setInterval(() => {
    const current = slides[index];
    index = (index + 1) % slides.length;
    const next = slides[index];
    if (hasGsap && !prefersReducedMotion) {
      gsap.to(current, { opacity: 0, duration: 0.5, ease: 'power1.inOut' });
      gsap.fromTo(next, { opacity: 0 }, { opacity: 1, duration: 0.5, ease: 'power1.inOut', delay: 0.1 });
    } else {
      current.style.opacity = 0;
      next.style.opacity = 1;
    }
  }, 4800);
})();
